import { useCallback, useEffect, useState } from "react";
import {
  getAutoVaultState,
  getFirstRolloverTime,
  getPreRoundSharePriceOfFarm,
  getPriceRangeOfFarm,
  getTotalSupplyOfFarm,
} from "../utils/api/autoFarmApi";
import { getAutoVaultCumulativeFeeFromGinza } from "../utils/api/ginzaApi";

const useAutoVaultState = (
  factoryAddress: string,
  farmAddress: string,
  farmActionAddress: string,
  tokenDecimal: number,
  deployedChainId: number
) => {
  const [priceRange, setPriceRange] = useState<{
    upperTickPrice: number;
    lowerTickPrice: number;
  }>({ upperTickPrice: 0, lowerTickPrice: 0 });
  const [totalSupply, setTotalSupply] = useState<number>(0);
  const [prevRoundSharePrice, setPrevRoundSharePrice] = useState<number>(0);
  const [startTimestamp, setStartTimestamp] = useState<number>(0);
  const [apr, setApr] = useState<number>(0);
  const [cumulativeFee, setCumulativeFee] = useState<number>(0);
  const [vaultState, setVaultState] = useState<number>(0); // 0:Locked, 1:Unlocked, 2:Emergency

  const getPriceRange = useCallback(async () => {
    const { upperTickPrice, lowerTickPrice } = await getPriceRangeOfFarm(
      factoryAddress,
      farmActionAddress,
      deployedChainId
    );
    setPriceRange({ upperTickPrice, lowerTickPrice });
  }, [factoryAddress, farmActionAddress, deployedChainId]);

  const getTotalSupply = useCallback(async () => {
    const amount = await getTotalSupplyOfFarm(
      farmAddress,
      tokenDecimal,
      deployedChainId
    );
    setTotalSupply(+amount);
  }, [farmAddress, tokenDecimal, deployedChainId]);

  const getPreviousRoundSharePrice = useCallback(async () => {
    const sharePrice = await getPreRoundSharePriceOfFarm(
      farmAddress,
      tokenDecimal,
      deployedChainId
    );
    setPrevRoundSharePrice(+sharePrice);
  }, [farmAddress, tokenDecimal, deployedChainId]);

  const getStartTimestamp = useCallback(async () => {
    const timestamp = await getFirstRolloverTime(
      farmActionAddress,
      deployedChainId
    );
    setStartTimestamp(+timestamp);
  }, [farmActionAddress, deployedChainId]);

  const getCumulativeFee = useCallback(async () => {
    const fee = await getAutoVaultCumulativeFeeFromGinza();
    setCumulativeFee(+fee);
  }, []);

  const getVaultState = useCallback(async () => {
    const state = await getAutoVaultState(farmAddress, deployedChainId);
    setVaultState(state);
  }, [farmAddress, deployedChainId]);

  const updateAutoVaultState = useCallback(() => {
    getTotalSupply();
  }, [getTotalSupply]);

  useEffect(() => {
    if (prevRoundSharePrice && startTimestamp) {
      const currTimestamp = Math.floor(new Date().getTime() / 1000);
      const num =
        (prevRoundSharePrice - 1) *
        (365 / ((currTimestamp - startTimestamp) / (60 * 60 * 24)));
      setApr(num * 100);
    }
  }, [prevRoundSharePrice, startTimestamp]);

  useEffect(() => {
    getPriceRange();
    getPreviousRoundSharePrice();
    getStartTimestamp();
    getCumulativeFee();
    getVaultState();

    // States that will change if users deposit/withdraw
    updateAutoVaultState();
  }, [
    getPriceRange,
    getPreviousRoundSharePrice,
    getStartTimestamp,
    getCumulativeFee,
    getVaultState,
    updateAutoVaultState,
  ]);

  return {
    updateAutoVaultState,
    priceRange,
    totalSupply,
    prevRoundSharePrice,
    startTimestamp,
    apr,
    cumulativeFee,
    vaultState,
  };
};

export default useAutoVaultState;
